// Organization lookups fetched once and reused. The data is read-only seed data in v1 so we do
// not need cache invalidation; the pages that mutate (none in v1) would `refresh()` after a
// success.

import { useEffect, useState } from 'react';
import { api } from './api-client';
import type {
  DepartmentDto,
  EmploymentStatusDto,
  JobTitleDto,
  LocationDto,
} from './types';

export type OrgData = {
  departments: DepartmentDto[];
  locations: LocationDto[];
  jobTitles: JobTitleDto[];
  employmentStatuses: EmploymentStatusDto[];
};

let cached: OrgData | undefined;
let inflight: Promise<OrgData> | undefined;

async function fetchOrg(): Promise<OrgData> {
  const [departments, locations, jobTitles, employmentStatuses] = await Promise.all([
    api.get<DepartmentDto[]>('/v1/organization/departments'),
    api.get<LocationDto[]>('/v1/organization/locations'),
    api.get<JobTitleDto[]>('/v1/organization/job-titles'),
    api.get<EmploymentStatusDto[]>('/v1/organization/employment-statuses'),
  ]);
  cached = { departments, locations, jobTitles, employmentStatuses };
  return cached;
}

// React hook that returns the organization lookups. Resolves to undefined while loading.
export function useOrg(): OrgData | undefined {
  const [data, setData] = useState<OrgData | undefined>(cached);

  useEffect(() => {
    if (cached) return;
    inflight ??= fetchOrg();
    inflight.then(setData).catch(() => undefined);
  }, []);

  return data;
}

// Employment statuses an employee may actually be put on. A status coded TERMINATED is never
// offered: End Employment is the only path that terminates someone, and the server rejects the write
// anyway. Migration V24_001 deleted the seeded row and Settings now refuses to mint the code again,
// so only a row predating that refusal can still reach this feed. Filtering only the offered choices
// keeps the feed itself whole, since it is also how historical rows resolve their label.
export function assignableEmploymentStatuses(
  statuses: EmploymentStatusDto[] | undefined,
): EmploymentStatusDto[] {
  return (statuses ?? []).filter((s) => s.code !== 'TERMINATED');
}

export function findName<T extends { id: number; name: string }>(
  list: T[] | undefined,
  id: number | null,
): string {
  if (!Array.isArray(list) || id === null) return '';
  return list.find((x) => x.id === id)?.name ?? '';
}
